<script setup lang="ts">
import { computed } from 'vue'

import { RepositoryTimeline } from '@types'

import { cn } from '@ds/lib/utils'
import { buildAxisPeriods } from './utils/helpers'

type Props = {
  /** Oldest-first period end dates — the whole range is drawn on the axis at once, nothing scrolls. */
  dates: string[]
  freq: RepositoryTimeline['freq']
  selected?: string
  disabled?: boolean
  /** Ticks only, no month labels — the phone-width layout from the design mock, where labels drop off the axis. */
  dense?: boolean
}

type Emits = {
  select: [date: string]
}

const { dates, freq, selected, disabled = false, dense = false } = defineProps<Props>()
const emit = defineEmits<Emits>()

defineOptions({
  name: 'DSPeriodScrollerAxis',
})

const periods = computed(() => buildAxisPeriods(dates, freq))

const currentDateIndex = computed(() => periods.value.findIndex(period => period.date === selected))

const periodWidth = computed(() => 100 / periods.value.length)
const periodStyle = computed(() => ({
  width: currentDateIndex.value > 0 ? `${periodWidth.value}%` : `calc(${periodWidth.value}% + 16px)`,
  right: currentDateIndex.value > 0 ? `${(100 - currentDateIndex.value * periodWidth.value)}%` : `calc(${(100 - currentDateIndex.value * periodWidth.value)}% + 8px)`,
  backgroundImage: `repeating-linear-gradient(
    -45deg,
    rgb(var(--ds-muted)) 0,
    rgb(var(--ds-muted)) 5px,
    rgb(var(--ds-card)) 5px,
    rgb(var(--ds-card)) 10px
  )`,
}))

const tickClass = (isSelected: boolean) => cn(
  'block transition-[height,border-color] duration-ds-fast',
  dense
    ? isSelected
      ? 'mt-0 h-3.5 border-l-[3px] border-primary'
      : 'mt-0.75 h-2 border-l border-strong'
    : isSelected
      ? 'mt-0.5 h-3.75 border-l-2 border-primary'
      : 'mt-0.5 h-2 border-l border-strong',
)
</script>

<template>
  <div
    class="relative"
    :class="dense ? 'h-7.5' : 'h-14'"
  >
    <div
      class="absolute -top-2.5 -bottom-1 sm:-inset-y-1 translate-x-1/2 bg-white transition-[width,right]"
      :style="periodStyle"
    />
    <div
      class="absolute inset-x-0 h-px bg-border"
      :class="dense ? 'top-1.75' : 'top-6.75'"
    />
    <div
      class="absolute inset-x-0 h-0.75 rounded-xs bg-primary/16"
      :class="dense ? 'top-1.5' : 'top-6.5'"
    />
    <div class="relative flex h-full items-stretch">
      <button
        v-for="period in periods"
        :key="period.date"
        type="button"
        :data-date="period.date"
        :title="period.date"
        :aria-pressed="period.date === selected"
        :disabled
        :class="cn(
          'relative flex min-w-0 flex-1 flex-col items-center border-0 bg-transparent p-0 cursor-pointer',
          'disabled:pointer-events-none disabled:opacity-50',
          'focus-visible:outline-none focus-visible:shadow-ds-focus rounded-ds-sm',
          dense ? 'h-8 -mt-1.75 -mb-1.75 gap-0.5 pt-1.75' : 'h-full gap-1',
        )"
        @click="emit('select', period.date)"
      >
        <span
          v-if="!dense"
          class="inline-flex h-5 items-center rounded-full text-[0.71875rem] font-bold leading-none tracking-ds-tight transition-colors duration-ds-fast"
          :class="period.date === selected
            ? 'px-2.25 bg-primary text-primary-foreground shadow-ds-sm'
            : 'px-0 text-foreground'"
        >{{ period.label }}</span>
        <span :class="tickClass(period.date === selected)" />
        <span
          class="whitespace-nowrap text-[0.625rem] font-bold leading-none text-muted-foreground"
          :class="dense ? 'text-[0.59375rem] tracking-[0.04em]' : 'tracking-ds-wide'"
        >{{ period.isYearStart ? (dense ? period.yearShort : period.yearFull) : '' }}</span>
      </button>
    </div>
  </div>
</template>
